// src/components/open-houses/checkin/TemplateEditorModal.jsx
// Per-agent question templates for the check-in kiosk. Core questions are
// locked (name, contact, intent, group size); agents toggle the agent-status
// question and add extra yes/no, multiple choice, or short text questions.
import { useEffect, useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../../context/StateProvider";
import { useActionDialog } from "../../ActionDialog";
import {
  listenCheckinTemplates,
  saveCheckinTemplate,
  updateCheckinTemplate,
  deleteCheckinTemplate,
} from "../../../utils/firebasefunctions";

const blankQuestion = () => ({
  id: `q${Date.now()}${Math.floor(Math.random() * 1000)}`,
  type: "yesno",
  prompt: "",
  options: [],
  skippable: true,
});

const blankForm = () => ({
  name: "",
  askAgentStatus: true,
  questions: [],
});

export default function TemplateEditorModal({ open, onClose }) {
  const [{ user }] = useStateValue();
  const { confirm, dialog } = useActionDialog();
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // null = list view
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !user?.id) return;
    const unsub = listenCheckinTemplates(user.id, setTemplates);
    return () => unsub && unsub();
  }, [open, user?.id]);

  if (!open) return null;

  const startNew = () => { setForm(blankForm()); setEditing("new"); };
  const startEdit = (t) => {
    setForm({ name: t.name || "", askAgentStatus: t.askAgentStatus !== false, questions: t.questions || [] });
    setEditing(t.id);
  };

  const setQ = (i, patch) =>
    setForm((f) => ({
      ...f,
      questions: f.questions.map((q, j) => (j === i ? { ...q, ...patch } : q)),
    }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Give the template a name."); return; }
    const bad = form.questions.find((q) => !q.prompt.trim());
    if (bad) { toast.error("Every question needs its wording."); return; }
    const badChoice = form.questions.find(
      (q) => q.type === "choice" && q.options.filter((o) => o.trim()).length < 2,
    );
    if (badChoice) { toast.error("Multiple choice questions need at least 2 options."); return; }
    setSaving(true);
    const payload = {
      ownerId: user.id,
      name: form.name.trim(),
      askAgentStatus: form.askAgentStatus,
      questions: form.questions.map((q) => ({
        ...q,
        prompt: q.prompt.trim(),
        options: q.type === "choice" ? q.options.filter((o) => o.trim()) : [],
      })),
    };
    const r = editing === "new"
      ? await saveCheckinTemplate(payload)
      : await updateCheckinTemplate(editing, payload);
    setSaving(false);
    if (r.success) { toast.success("Template saved."); setEditing(null); }
    else toast.error("Could not save the template. Try again.");
  };

  const remove = async (t) => {
    const ok = await confirm({
      title: "Delete template",
      body: `Delete the "${t.name}" template?`,
      tone: "danger",
      confirmLabel: "Delete",
    });
    if (!ok) return;
    const r = await deleteCheckinTemplate(t.id);
    if (r.success) toast.success("Template deleted.");
    else toast.error("Could not delete. Try again.");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Check-In Questions</h2>
            <p className="text-xs text-slate-500">
              Name, contact info, plans, and group size are always asked. Templates control the extras.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          {editing === null ? (
            <>
              {templates.length === 0 && (
                <p className="text-sm text-slate-500 mb-4">
                  No templates yet. The built-in Standard set is always available at kiosk start.
                </p>
              )}
              <div className="space-y-2">
                {templates.map((t) => (
                  <div key={t.id} className="flex items-center justify-between border border-slate-200 rounded-lg px-4 py-3">
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">{t.name}</div>
                      <div className="text-xs text-slate-500">
                        {(t.questions || []).length} extra question{(t.questions || []).length === 1 ? "" : "s"}
                        {t.askAgentStatus === false ? " · agent question off" : ""}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => startEdit(t)} className="text-xs font-semibold text-slate-700 hover:text-slate-900 border border-slate-300 rounded-md px-3 py-1.5">Edit</button>
                      <button onClick={() => remove(t)} aria-label={`Delete ${t.name}`} className="text-slate-400 hover:text-red-600"><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={startNew} className="mt-4 inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold px-3 py-2 rounded-md">
                <Plus size={16} /> New Template
              </button>
            </>
          ) : (
            <>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Template name</label>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder='e.g. "Luxury listings"'
                className="w-full border border-slate-300 rounded-lg p-2 mb-4 outline-none focus:border-slate-800"
              />
              <label className="flex items-center gap-2 text-sm text-slate-700 mb-4">
                <input
                  type="checkbox"
                  checked={form.askAgentStatus}
                  onChange={(e) => setForm((f) => ({ ...f, askAgentStatus: e.target.checked }))}
                />
                Ask "Are you working with an agent?"
              </label>

              <div className="space-y-3">
                {form.questions.map((q, i) => (
                  <div key={q.id} className="border border-slate-200 rounded-lg p-3">
                    <div className="flex gap-2 mb-2">
                      <select
                        value={q.type}
                        onChange={(e) => setQ(i, { type: e.target.value })}
                        className="border border-slate-300 rounded-md text-sm p-1.5"
                      >
                        <option value="yesno">Yes / No</option>
                        <option value="choice">Multiple choice</option>
                        <option value="text">Short answer</option>
                      </select>
                      <input
                        value={q.prompt}
                        onChange={(e) => setQ(i, { prompt: e.target.value })}
                        placeholder="Question wording, e.g. How did you hear about this open house?"
                        className="flex-1 border border-slate-300 rounded-md text-sm p-1.5 outline-none focus:border-slate-800"
                      />
                      <button
                        onClick={() => setForm((f) => ({ ...f, questions: f.questions.filter((_, j) => j !== i) }))}
                        aria-label="Remove question"
                        className="text-slate-400 hover:text-red-600"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    {q.type === "choice" && (
                      <input
                        value={(q.options || []).join(", ")}
                        onChange={(e) => setQ(i, { options: e.target.value.split(",").map((s) => s.trimStart()) })}
                        placeholder="Options, separated by commas: Sign, Zillow, Friend"
                        className="w-full border border-slate-300 rounded-md text-sm p-1.5 outline-none focus:border-slate-800"
                      />
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={() => setForm((f) => ({ ...f, questions: [...f.questions, blankQuestion()] }))}
                className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-slate-700 border border-slate-300 rounded-md px-3 py-1.5 hover:bg-slate-50"
              >
                <Plus size={14} /> Add question
              </button>

              <div className="flex justify-end gap-2 mt-6">
                <button onClick={() => setEditing(null)} className="text-sm font-semibold text-slate-600 px-4 py-2">Cancel</button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-md"
                >
                  {saving ? "Saving..." : "Save Template"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {dialog}
    </div>
  );
}
